import type { FC } from "react";
import { useId, useMemo } from "react";

import { ReplayIcon } from "@jti/brand-icons/replay";
import { SelectionTileItem } from "@jti/core-components";
import { Button } from "@jti/core-components/components/Button";
import { Checkbox } from "@jti/core-components/components/Checkbox";
import {
  DrawerBackdrop,
  DrawerBody,
  DrawerCloseTrigger,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerRoot,
  DrawerTrigger,
} from "@jti/ui/components/Drawer";
import { Expandable } from "@jti/ui/components/Expandable";
import { useUser } from "@jti/ui/hooks/useUser";

import type { PointsRangeItemDefinition, PointsRangeValue } from "./PointsRange";
import { PointsRange } from "./PointsRange";
import type { SortByItemDefinition, SortByValue } from "./SortBy";
import { SortBy } from "./SortBy";

import styles from "./SimplifiedPrizesFilters.module.scss";

type SimplifiedPrizesFiltersSortByProps = {
  isSortByFilterEnabled?: boolean;
  sortByFilterLabel?: string;
  sortByFilterItems?: SortByItemDefinition[];
  sortByValue?: SortByValue;
  onSortByChange: (value: SortByValue) => void;
};

type SimplifiedPrizesFiltersPointsRangeProps = {
  isPointsRangeFilterEnabled?: boolean;
  pointsRangeFilterLabel?: string;
  pointsRangeAllRangesLabel?: string;
  pointsRangeFilterItems?: PointsRangeItemDefinition[];
  pointsRangeValue?: PointsRangeValue;
  onPointsRangeChange: (value: PointsRangeValue) => void;
};

/**
 * This component is a base for additional filters that should be added in latter tickets, eveything is hidden behind props presence
 * this comment should be deleted once full implementaion is in place
 */
export type SimplifiedPrizesCategoryOption = {
  id: string;
  label: string;
  disabled?: boolean;
};

export type SimplifiedPrizesSubcategoryOption = {
  id: string;
  label: string;
  parentCategoryId: string;
};

export type SimplifiedPrizesFiltersProps = {
  filtersDesktopTitle: string;
  filtersSubtitle: string;
  filtersDialogAriaLabel?: string;
  resetFiltersButtonLabel: string;
  triggerButtonLabel?: string;
  activeFiltersCount?: number;
  applyButtonLabel?: string;
  categoryFilterLabel?: string;
  isCategoryFilterEnabled?: boolean;
  categoryOptions?: SimplifiedPrizesCategoryOption[];
  selectedCategoryId?: string;
  onCategoryChange?: (categoryId: string) => void;
  subcategoryFilterLabel?: string;
  isSubcategoryFilterEnabled?: boolean;
  subcategoryOptions?: SimplifiedPrizesSubcategoryOption[];
  selectedSubcategoryIds?: string[];
  onSubcategoryIdsChange?: (subcategoryIds: string[]) => void;
  pointsRangeFilterLabel?: string;
  isClubPloomTiersFilterEnabled?: boolean;
  clubPloomTiersFilterLabel?: string;
  silverTierLabel?: string;
  goldTierLabel?: string;
  blackTierLabel?: string;
  selectedClubPloomTiers?: string[];
  onClubPloomTiersChange?: (tiers: string[]) => void;
  isAvailableForMeFilterEnabled?: boolean;
  availableForMeFilterLabel?: string;
  availableForMeDisabledFilterTooltipText?: string;
  isAvailableForMeChecked?: boolean;
  onAvailableForMeChange?: (checked: boolean) => void;
  onApplyFilters?: () => void;
  onResetFilters?: () => void;
  onCancelFilters?: () => void;
} & SimplifiedPrizesFiltersSortByProps &
  SimplifiedPrizesFiltersPointsRangeProps;

export const SimplifiedPrizesFilters: FC<SimplifiedPrizesFiltersProps> = ({
  applyButtonLabel,
  triggerButtonLabel,
  activeFiltersCount,
  resetFiltersButtonLabel,
  categoryFilterLabel,
  isCategoryFilterEnabled,
  categoryOptions,
  selectedCategoryId,
  onCategoryChange,
  subcategoryFilterLabel,
  isSubcategoryFilterEnabled,
  subcategoryOptions,
  selectedSubcategoryIds = [],
  onSubcategoryIdsChange,
  isClubPloomTiersFilterEnabled,
  clubPloomTiersFilterLabel,
  silverTierLabel,
  goldTierLabel,
  blackTierLabel,
  selectedClubPloomTiers = [],
  onClubPloomTiersChange,
  isAvailableForMeFilterEnabled,
  availableForMeFilterLabel,
  availableForMeDisabledFilterTooltipText,
  isAvailableForMeChecked,
  onAvailableForMeChange,
  isSortByFilterEnabled,
  sortByFilterLabel,
  sortByFilterItems,
  sortByValue,
  onSortByChange,
  isPointsRangeFilterEnabled,
  pointsRangeFilterLabel,
  pointsRangeAllRangesLabel,
  pointsRangeFilterItems,
  pointsRangeValue,
  onPointsRangeChange,
  onApplyFilters,
  onResetFilters,
  onCancelFilters,
  filtersDesktopTitle,
  filtersSubtitle,
  filtersDialogAriaLabel,
}) => {
  const filtersTitleId = useId();
  const { isUserLoggedIn } = useUser();

  const isAvailableForMeActive = Boolean(isAvailableForMeChecked);
  const availableForMeDisabledFilterTooltip = availableForMeDisabledFilterTooltipText?.replace(
    "{0}",
    availableForMeFilterLabel ?? ""
  );

  const clubPloomTiers = [
    { value: "Silver", label: silverTierLabel, swatchClassName: styles.tierSwatchSilver },
    { value: "Gold", label: goldTierLabel, swatchClassName: styles.tierSwatchGold },
    { value: "Black", label: blackTierLabel, swatchClassName: styles.tierSwatchBlack },
  ].filter((tier): tier is { value: string; label: string; swatchClassName: string } => Boolean(tier.label));

  const handleTierChange = (value: string) => {
    const isSelected = selectedClubPloomTiers.includes(value);
    const nextTiers = isSelected
      ? selectedClubPloomTiers.filter((tier) => tier !== value)
      : [...selectedClubPloomTiers, value];
    onClubPloomTiersChange?.(nextTiers);
  };

  const disabledCategoryIds = useMemo(
    () => new Set(categoryOptions?.filter((option) => option.disabled).map((option) => option.id) ?? []),
    [categoryOptions]
  );

  const visibleSubcategoryOptions = useMemo(() => {
    if (!subcategoryOptions?.length) return [];

    const optionsWithoutDisabledCategories = subcategoryOptions.filter(
      (option) => !disabledCategoryIds.has(option.parentCategoryId)
    );

    if (!selectedCategoryId) return optionsWithoutDisabledCategories;

    return optionsWithoutDisabledCategories.filter((option) => option.parentCategoryId === selectedCategoryId);
  }, [disabledCategoryIds, selectedCategoryId, subcategoryOptions]);

  const handleSubcategoryToggle = (subcategoryId: string, parentCategoryId: string) => {
    const isSelected = selectedSubcategoryIds.includes(subcategoryId);
    const nextIds = isSelected
      ? selectedSubcategoryIds.filter((id) => id !== subcategoryId)
      : [...selectedSubcategoryIds, subcategoryId];

    onSubcategoryIdsChange?.(nextIds);

    if (!isSelected && selectedCategoryId !== parentCategoryId) {
      onCategoryChange?.(parentCategoryId);
    }
  };

  return (
    <DrawerRoot onCancelCallback={onCancelFilters}>
      <DrawerTrigger>
        <Button variant="text" size="lg">
          {activeFiltersCount ? `${triggerButtonLabel} (${activeFiltersCount})` : (triggerButtonLabel ?? "")}
        </Button>
      </DrawerTrigger>
      <DrawerContent isMobileFullScreen aria-label={filtersDialogAriaLabel}>
        <DrawerHeader hasBackButton backButtonLabel={"Back"}>
          <h2 id={filtersTitleId} className={styles.filtersTitle}>
            {filtersDesktopTitle}
          </h2>
        </DrawerHeader>
        <DrawerBody isPaddingDisabled>
          <div className={styles.filtersContent}>
            <div className={styles.filtersContentHeader}>
              <span className={styles.filtersSubtitle}>{filtersSubtitle}</span>
              <button type="button" className={styles.filtersResetButton} onClick={onResetFilters}>
                <ReplayIcon aria-hidden="true" className={styles.filtersResetIcon} />
                {resetFiltersButtonLabel}
              </button>
            </div>
            <div className={styles.filtersInnerContent}>
              {isUserLoggedIn && isAvailableForMeFilterEnabled && availableForMeFilterLabel && (
                <Checkbox
                  checked={Boolean(isAvailableForMeChecked)}
                  label={availableForMeFilterLabel}
                  onCheckedChange={(checked) => onAvailableForMeChange?.(checked)}
                />
              )}
              {isSortByFilterEnabled && sortByFilterLabel && sortByFilterItems?.length && onSortByChange && (
                <Expandable defaultExpanded label={sortByFilterLabel}>
                  <SortBy items={sortByFilterItems} value={sortByValue} onChange={onSortByChange} />
                </Expandable>
              )}
              {categoryFilterLabel && isCategoryFilterEnabled && (
                <Expandable defaultExpanded label={categoryFilterLabel}>
                  <fieldset className={styles.filtersCategoryFieldset} aria-label={categoryFilterLabel}>
                    {categoryOptions?.map((option) => (
                      <SelectionTileItem
                        key={option.id}
                        type="radio"
                        name={`categoryFilter-${option.id}`}
                        value={option.id}
                        label={option.label}
                        checked={selectedCategoryId === option.id}
                        disabled={option.disabled}
                        onChange={() => onCategoryChange?.(option.id)}
                      />
                    ))}
                  </fieldset>
                </Expandable>
              )}
              {subcategoryFilterLabel && isCategoryFilterEnabled && isSubcategoryFilterEnabled && (
                <Expandable label={subcategoryFilterLabel}>
                  <fieldset className={styles.clubPloomTiersFieldset} aria-label={subcategoryFilterLabel}>
                    {visibleSubcategoryOptions.map((option) => (
                      <SelectionTileItem
                        key={option.id}
                        type="checkbox"
                        name={`subcategoryFilter-${option.id}`}
                        value={option.id}
                        label={option.label}
                        checked={selectedSubcategoryIds.includes(option.id)}
                        onChange={() => handleSubcategoryToggle(option.id, option.parentCategoryId)}
                      />
                    ))}
                  </fieldset>
                </Expandable>
              )}
              {isPointsRangeFilterEnabled && pointsRangeFilterLabel && pointsRangeAllRangesLabel && (
                <Expandable
                  label={pointsRangeFilterLabel}
                  disabled={isAvailableForMeActive}
                  disabledTooltip={availableForMeDisabledFilterTooltip}
                >
                  <PointsRange
                    items={pointsRangeFilterItems ?? []}
                    allRangesLabel={pointsRangeAllRangesLabel}
                    value={pointsRangeValue}
                    onChange={onPointsRangeChange}
                  />
                </Expandable>
              )}
              {isClubPloomTiersFilterEnabled && clubPloomTiersFilterLabel && clubPloomTiers.length > 0 && (
                <Expandable
                  label={clubPloomTiersFilterLabel}
                  disabled={isAvailableForMeActive}
                  disabledTooltip={availableForMeDisabledFilterTooltip}
                >
                  <fieldset className={styles.clubPloomTiersFieldset} aria-label={clubPloomTiersFilterLabel}>
                    {clubPloomTiers.map((tier) => (
                      <SelectionTileItem
                        key={tier.value}
                        type="checkbox"
                        name="clubPloomTiers"
                        value={tier.value}
                        label={tier.label}
                        icon={<span aria-hidden="true" className={`${styles.tierSwatch} ${tier.swatchClassName}`} />}
                        checked={selectedClubPloomTiers.includes(tier.value)}
                        onChange={() => handleTierChange(tier.value)}
                      />
                    ))}
                  </fieldset>
                </Expandable>
              )}
            </div>
          </div>
        </DrawerBody>

        <DrawerFooter>
          <DrawerCloseTrigger>
            <div className={styles.filtersApplyButtonWrapper}>
              <Button size="lg" onClick={onApplyFilters}>
                {applyButtonLabel ?? ""}
              </Button>
            </div>
          </DrawerCloseTrigger>
        </DrawerFooter>
      </DrawerContent>
      <DrawerBackdrop />
    </DrawerRoot>
  );
};
